import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, MapPin, Grid3x3, Eye, Building2, Plane, Hotel,
  Globe, Zap, Calculator, Database, BarChart3,
  Users, Smartphone, Monitor, Wrench, ChevronDown, Check,
  Shield, DollarSign
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import AccordionSection from '../components/AccordionSection';

const ImplementationPage = () => {
  const { t, language } = useLanguage();
  const getLocalizedPath = (path) => `/${language}${path}`;

  const deploymentExamples = t('implementation.deploymentExamples.items') || [];
  const scaleMetrics = t('implementation.enterpriseScale.metrics') || [];
  const facilityTypes = t('implementation.calculator.facilityTypes') || [];
  const scaleIcons = [Globe, Grid3x3, Zap];

  const [facilityType, setFacilityType] = useState('');
  const [customFacilityType, setCustomFacilityType] = useState('');
  const [facilitySize, setFacilitySize] = useState('');
  const [numLocations, setNumLocations] = useState('');
  const [calcResult, setCalcResult] = useState(null);

  const handleCalculate = (e) => {
    e.preventDefault();
    const size = parseInt(facilitySize) || 0;
    const locations = parseInt(numLocations) || 1;
    if (size <= 0) return;
    const tagsPerFacility = Math.max(8, Math.round(size / 2800));
    const zones = Math.max(2, Math.round(size / 22000));
    setCalcResult({
      size: size.toLocaleString(),
      tags: tagsPerFacility * locations,
      tagsPerFacility,
      zones: zones * locations,
      zonesPerFacility: zones,
      reportsLow: Math.round(tagsPerFacility * 1.8 * locations),
      reportsHigh: Math.round(tagsPerFacility * 4.5 * locations),
      locations,
    });
  };

  return (
    <div className="min-h-screen" data-testid="implementation-page">

      {/* ========== 1. HERO ========== */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-gradient-to-br from-sky-100 to-blue-50 rounded-full blur-3xl" />
        </div>
        <div className="container-max px-6 md:px-12 lg:px-24 relative">
          <div className="text-center max-w-4xl mx-auto">
            <span className="text-caption text-[#0077FF] mb-4 block" data-testid="implementation-label">
              {t('implementation.hero.label')}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-slate-900 leading-tight tracking-tight mb-6" data-testid="implementation-title">
              {t('implementation.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
              {t('implementation.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* ========== 1b. RISK-BASED DEPLOYMENT MODELS ========== */}
      <section className="section-padding bg-white border-b border-slate-100" data-testid="risk-deployment-models-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-sm font-bold text-blue-600 uppercase tracking-widest mb-4 block">Risk Intelligence</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Risk-Based Deployment Models</h2>
            <p className="text-lg text-slate-600">How deployment scale and coverage intensity are determined by operational risk analysis — not guesswork.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <Calculator className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Deployment Sizing</h3>
              <p className="text-sm text-slate-600">Tag count and zone coverage are calculated from square footage, entry points, and facility layout — scaled per location.</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Risk Coverage</h3>
              <p className="text-sm text-slate-600">Industry-specific incident benchmarks define minimum and maximum coverage thresholds per risk zone classification.</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <DollarSign className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Exposure Modeling</h3>
              <p className="text-sm text-slate-600">Financial liability is modeled from incident probability, claim severity, and disruption cost — specific to each vertical.</p>
            </div>
          </div>
          <div className="text-center mt-10">
            <Link to={getLocalizedPath('/operational-risk')} className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors" data-testid="link-to-risk-model">
              Explore the full Model Risk Coverage calculator <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== 2. DEPLOYMENT OVERVIEW — 4 Cards ========== */}
      <section className="section-padding bg-white" data-testid="deployment-overview-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('implementation.deployOverview.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('implementation.deployOverview.title')}
            </h2>
            <p className="text-lg text-slate-600">
              {t('implementation.deployOverview.subtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(t('implementation.deployOverview.steps') || []).map((step, index) => {
              const icons = [MapPin, Smartphone, Monitor, Zap];
              const colors = [
                { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20', num: 'bg-[#0077FF]' },
                { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', num: 'bg-amber-600' },
                { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200', num: 'bg-emerald-600' },
                { bg: 'bg-violet-50', iconBg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200', num: 'bg-violet-600' }
              ];
              const Ic = icons[index]; const c = colors[index];
              return (
                <div key={index} className={`${c.bg} rounded-2xl p-8 border ${c.border} relative`}>
                  <div className={`absolute top-4 right-4 w-8 h-8 ${c.num} rounded-full flex items-center justify-center`}>
                    <span className="text-white text-sm font-bold">{index + 1}</span>
                  </div>
                  <div className={`w-14 h-14 ${c.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                    <Ic className={`w-7 h-7 ${c.text}`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== 3. EXAMPLE SMART TAG DEPLOYMENT ========== */}
      <section className="section-padding bg-slate-50" data-testid="deployment-diagram-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-caption text-[#0077FF] mb-4 block">Facility Layout</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('implementation.diagram.title')}
            </h2>
            <p className="text-lg text-slate-600">
              {t('implementation.diagram.description')}
            </p>
          </div>
          <div className="max-w-[1100px] mx-auto mb-6">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
              <img loading="eager" decoding="sync" src="/deployment_warehouse.png" alt="Tapelligence Smart Tag Deployment" className="w-full h-auto" data-testid="deployment-diagram-image" />
            </div>
          </div>
          <p className="text-sm text-slate-500 text-center max-w-2xl mx-auto">
            {t('implementation.diagram.caption')}
          </p>
        </div>
      </section>

      {/* ========== 4. HUMAN PRESENCE INTELLIGENCE — Progressive Disclosure ========== */}
      <section className="section-padding bg-white" data-testid="human-presence-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <AccordionSection
            testId="human-presence-accordion"
            expandLabel={t('implementation.accordion.showPresence')}
            collapseLabel={t('implementation.accordion.hidePresence')}
            visibleContent={
              <>
                <div className="text-center max-w-3xl mx-auto mb-10">
                  <span className="text-caption text-[#0077FF] mb-4 block">{t('implementation.humanPresence.label')}</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                    {t('implementation.humanPresence.title')}
                  </h2>
                  <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                    <p>{t('implementation.humanPresence.intro1')}</p>
                    <p>{t('implementation.humanPresence.intro2')}</p>
                  </div>
                </div>
                <div className="max-w-[1100px] mx-auto mb-6 overflow-hidden rounded-2xl">
                  <img loading="eager" decoding="sync" src="/human_presence_intelligence.png" alt="Human Presence Becomes Operational Intelligence" className="w-full h-auto -mt-[3%]" data-testid="human-presence-image" />
                </div>
                <div className="max-w-3xl mx-auto text-center mb-8">
                  <p className="text-sm font-semibold text-slate-800 mb-1">{t('implementation.humanPresence.captionTitle')}</p>
                  <p className="text-sm text-slate-500">{t('implementation.humanPresence.captionText')}</p>
                </div>
                <div className="max-w-3xl mx-auto text-center">
                  <p className="text-xl font-bold text-slate-900">{t('implementation.humanPresence.loop')}</p>
                </div>
              </>
            }
            expandableContent={
              <div className="max-w-3xl mx-auto space-y-4 text-lg text-slate-600 leading-relaxed text-center mt-6">
                <p>{t('implementation.humanPresence.reinforce1')}</p>
              </div>
            }
          />
        </div>
      </section>

      {/* ========== 5. HAZARD RESPONSE FLOW — Progressive Disclosure ========== */}
      <section className="section-padding bg-slate-50" data-testid="hazard-response-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <AccordionSection
            testId="hazard-response-accordion"
            expandLabel={t('implementation.accordion.showResponse')}
            collapseLabel={t('implementation.accordion.hideResponse')}
            visibleContent={
              <>
                <div className="text-center max-w-3xl mx-auto mb-10">
                  <span className="text-caption text-[#0077FF] mb-4 block">{t('implementation.hazardResponse.label')}</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                    {t('implementation.hazardResponse.title')}
                  </h2>
                </div>

                <div className="max-w-[1100px] mx-auto mb-8 overflow-hidden rounded-2xl">
                  <img loading="eager" decoding="sync" src="/hazard_response_flow.png" alt="Tapelligence Hazard Response Flow" className="w-full h-auto -mt-[2.5%]" data-testid="hazard-response-image" />
                </div>

                {/* 4-step process */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 max-w-5xl mx-auto">
                  {(t('implementation.hazardResponse.processSteps') || []).map((step, index) => {
                    const icons = [Zap, Smartphone, Monitor, Wrench];
                    const colors = [
                      { bg: 'bg-red-50', iconBg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200', num: 'bg-red-600' },
                      { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', num: 'bg-amber-600' },
                      { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20', num: 'bg-[#0077FF]' },
                      { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200', num: 'bg-emerald-600' }
                    ];
                    const Ic = icons[index]; const c = colors[index];
                    return (
                      <div key={index} className={`${c.bg} rounded-2xl p-6 border ${c.border} relative`}>
                        <div className={`absolute top-3 right-3 w-7 h-7 ${c.num} rounded-full flex items-center justify-center`}>
                          <span className="text-white text-xs font-bold">{index + 1}</span>
                        </div>
                        <div className={`w-12 h-12 ${c.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                          <Ic className={`w-6 h-6 ${c.text}`} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="text-center">
                  <p className="text-xl font-bold text-slate-900">{t('implementation.hazardResponse.explainArrow')}</p>
                </div>
              </>
            }
            expandableContent={
              <div className="max-w-3xl mx-auto mt-8">
                {/* Stakeholder Connection Flow */}
                <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
                  <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">
                    {[
                      { icon: Users, label: t('implementation.hazardResponse.flowCustomers'), sub: t('implementation.hazardResponse.flowCustomersSub'), color: 'sky' },
                      { icon: Smartphone, label: t('implementation.hazardResponse.flowTags'), sub: t('implementation.hazardResponse.flowTagsSub'), color: 'amber' },
                      { icon: Monitor, label: t('implementation.hazardResponse.flowManagers'), sub: t('implementation.hazardResponse.flowManagersSub'), color: 'emerald' },
                      { icon: Wrench, label: t('implementation.hazardResponse.flowEmployees'), sub: t('implementation.hazardResponse.flowEmployeesSub'), color: 'violet' },
                    ].map((node, i, arr) => (
                      <div key={i} className="flex flex-col md:flex-row items-center">
                        <div className="flex flex-col items-center">
                          <div className={`w-16 h-16 bg-${node.color}-100 rounded-full flex items-center justify-center mb-2`}>
                            <node.icon className={`w-8 h-8 text-${node.color}-600`} />
                          </div>
                          <span className="text-sm font-bold text-slate-800">{node.label}</span>
                          <span className="text-xs text-slate-500">{node.sub}</span>
                        </div>
                        {i < arr.length - 1 && (
                          <>
                            <div className="hidden md:flex items-center px-2">
                              <div className="w-12 h-0.5 bg-slate-300" />
                              <ArrowRight className="w-5 h-5 text-slate-400 -ml-1" />
                            </div>
                            <div className="md:hidden py-1">
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </section>

      {/* ========== 6. OPERATIONAL VISIBILITY ========== */}
      <section className="section-padding bg-white" data-testid="operational-visibility-facility-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('implementation.opVisibility.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              {t('implementation.opVisibility.title')}
            </h2>
            <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
              <p>{t('implementation.opVisibility.intro1')}</p>
              <p>{t('implementation.opVisibility.intro2')}</p>
            </div>
          </div>

          <div className="max-w-[1100px] mx-auto mb-6">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
              <img loading="eager" decoding="sync" src="/deployment_heatmap.png" alt="Operational Intelligence Visualization" className="w-full h-auto" data-testid="heatmap-image" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-6">
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-500" /><span className="text-slate-600">{t('implementation.opVisibility.legendRed')}</span></div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-orange-500" /><span className="text-slate-600">{t('implementation.opVisibility.legendOrange')}</span></div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green-500" /><span className="text-slate-600">{t('implementation.opVisibility.legendGreen')}</span></div>
            </div>
          </div>

          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-lg text-slate-600">{t('implementation.opVisibility.intro3')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {(t('implementation.opVisibility.cards') || []).map((card, index) => {
              const icons = [Database, MapPin, BarChart3];
              const colors = [
                { bg: 'bg-[#0077FF]/5', iconBg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20' },
                { bg: 'bg-amber-50', iconBg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' }
              ];
              const Ic = icons[index]; const c = colors[index];
              return (
                <div key={index} className={`${c.bg} rounded-2xl p-8 border ${c.border}`}>
                  <div className={`w-14 h-14 ${c.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                    <Ic className={`w-7 h-7 ${c.text}`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{card.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{card.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== 7. COMMAND CENTER ========== */}
      <section className="section-padding bg-slate-50" data-testid="impl-command-center-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-caption text-[#0077FF] mb-4 block">Command & Control</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('implementation.commandCenter.title')}
            </h2>
            <p className="text-lg text-slate-600">
              {t('implementation.commandCenter.subtitle')}
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">
                {t('implementation.commandCenter.textTitle')}
              </h3>
              <ul className="space-y-4">
                {(t('implementation.commandCenter.bullets') || []).map((bullet, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#0077FF]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-5 h-5 text-[#0077FF]" />
                    </div>
                    <span className="text-slate-700 font-medium">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative group">
              <div className="bg-slate-900 rounded-2xl p-1.5 shadow-2xl">
                <div className="bg-slate-800 rounded-t-xl px-4 py-2.5 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div className="flex-1 mx-3">
                    <div className="bg-slate-700 rounded-md px-3 py-1 text-slate-400 text-xs truncate">
                      portal.tapelligence.com/command
                    </div>
                  </div>
                </div>
                <div className="rounded-b-xl overflow-hidden">
                  <img loading="eager" decoding="sync" src="/screenshot_dashboard.png" alt="Tapelligence Command Center" className="w-full h-auto" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 8. STRUCTURED INCIDENT INTELLIGENCE ========== */}
      <section className="section-padding bg-white" data-testid="impl-incident-intelligence-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-caption text-[#0077FF] mb-4 block">Intelligence</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('implementation.incidentIntelligence.title')}
            </h2>
            <p className="text-lg text-slate-600">
              {t('implementation.incidentIntelligence.subtitle')}
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative group order-2 lg:order-1">
              <div className="bg-slate-900 rounded-2xl p-1.5 shadow-2xl">
                <div className="bg-slate-800 rounded-t-xl px-4 py-2.5 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div className="flex-1 mx-3">
                    <div className="bg-slate-700 rounded-md px-3 py-1 text-slate-400 text-xs truncate">
                      portal.tapelligence.com/analytics
                    </div>
                  </div>
                </div>
                <div className="rounded-b-xl overflow-hidden">
                  <img loading="eager" decoding="sync" src="/screenshot_analytics.png" alt="Incident Analytics Dashboard" className="w-full h-auto" />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">
                {t('implementation.incidentIntelligence.textTitle')}
              </h3>
              <ul className="space-y-4">
                {(t('implementation.incidentIntelligence.capabilities') || []).map((cap, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="text-slate-700 font-medium">{cap}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== 9. BRIDGE TO OPERATIONAL EXPERIENCE ========== */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" data-testid="adoption-bridge-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-sm font-bold text-orange-600 uppercase tracking-widest mb-4 block">{t('implementation.adoptionBridge.label')}</span>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{t('implementation.adoptionBridge.title')}</h2>
            <p className="text-base text-slate-600 leading-relaxed mb-8">
              {t('implementation.adoptionBridge.description')}
            </p>
            <Link to={getLocalizedPath('/operational-experience')} className="inline-flex items-center justify-center gap-2 bg-[#0077FF] hover:bg-[#0066DD] text-white px-6 py-3 rounded-xl font-semibold text-base transition-all shadow-sm hover:shadow-md active:scale-[0.98]" data-testid="adoption-bridge-cta">
              {t('implementation.adoptionBridge.cta')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>



      {/* ========== DEPLOYMENT CALCULATOR ========== */}
      <section className="section-padding bg-white scroll-mt-28" id="deployment-estimator" data-testid="deployment-calculator-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('implementation.calculator.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('implementation.calculator.title')}</h2>
            <p className="text-lg text-slate-600 mb-8">{t('implementation.calculator.description')}</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
              <div className="bg-[#0077FF] px-8 py-5 flex items-center gap-3 rounded-t-2xl">
                <Calculator className="w-5 h-5 text-white" />
                <h3 className="text-white font-semibold">{t('implementation.calculator.title')}</h3>
              </div>
              <form onSubmit={handleCalculate} className="p-8">
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('implementation.calculator.facilityTypeLabel')}</label>
                    <select value={facilityType} onChange={(e) => setFacilityType(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500" data-testid="calc-facility-type" required>
                      <option value="">{t('implementation.calculator.selectPlaceholder')}</option>
                      {facilityTypes.map((type, i) => (<option key={i} value={type}>{type}</option>))}
                    </select>
                    {facilityType === 'Other' && (
                      <input type="text" value={customFacilityType} onChange={(e) => setCustomFacilityType(e.target.value)} placeholder="Please specify your facility type" className="w-full mt-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500" data-testid="calc-custom-facility-type" required />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('implementation.calculator.facilitySizeLabel')}</label>
                    <input type="number" value={facilitySize} onChange={(e) => setFacilitySize(e.target.value)} placeholder={t('implementation.calculator.sizePlaceholder')} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500" data-testid="calc-facility-size" required min="1" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('implementation.calculator.numLocationsLabel')}</label>
                    <input type="number" value={numLocations} onChange={(e) => setNumLocations(e.target.value)} placeholder="1" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500" data-testid="calc-num-locations" required min="1" />
                  </div>
                </div>
                <button type="submit" className="btn-primary bg-[#0077FF] hover:bg-[#0066DD] text-base w-full md:w-auto" data-testid="calc-submit-btn">
                  {t('implementation.calculator.submitBtn')} <ArrowRight className="w-5 h-5" />
                </button>
              </form>
              {calcResult && (
                <div className="border-t border-slate-200 bg-slate-50 p-8" data-testid="calc-results">
                  <h4 className="text-lg font-bold text-slate-900 mb-6">{t('implementation.calculator.resultsTitle')}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                      <p className="font-bold text-[#0077FF] mb-1 leading-tight" style={{ fontSize: `${Math.max(16, Math.min(28, 28 - Math.max(0, String(calcResult.size).length - 6) * 2))}px` }}>{calcResult.size}</p>
                      <p className="text-xs text-slate-500">{t('implementation.calculator.resultSqFt')}</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                      <p className="font-bold text-[#0077FF] mb-1 leading-tight" style={{ fontSize: `${Math.max(16, Math.min(28, 28 - Math.max(0, String(calcResult.tags).length - 6) * 2))}px` }}>{calcResult.tags}</p>
                      <p className="text-xs text-slate-500">{t('implementation.calculator.resultTags')}</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                      <p className="font-bold text-[#0077FF] mb-1 leading-tight" style={{ fontSize: `${Math.max(16, Math.min(28, 28 - Math.max(0, String(calcResult.zones).length - 6) * 2))}px` }}>{calcResult.zones}</p>
                      <p className="text-xs text-slate-500">{t('implementation.calculator.resultZones')}</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 border border-slate-200 text-center">
                      <p className="font-bold text-[#0077FF] mb-1 leading-tight" style={{ fontSize: `${Math.max(14, Math.min(28, 28 - Math.max(0, String(calcResult.reportsLow + '–' + calcResult.reportsHigh).length - 6) * 1.5))}px` }}>{calcResult.reportsLow}–{calcResult.reportsHigh}</p>
                      <p className="text-xs text-slate-500">{t('implementation.calculator.resultReports')}</p>
                    </div>
                  </div>
                  {calcResult.locations > 1 && (
                    <p className="text-sm text-slate-500 mb-6">
                      {t('implementation.calculator.perFacility')}: {calcResult.tagsPerFacility} {t('implementation.calculator.resultTags').toLowerCase()}, {calcResult.zonesPerFacility} {t('implementation.calculator.resultZones').toLowerCase()}
                    </p>
                  )}
                  <Link to={getLocalizedPath('/contact')} className="inline-flex items-center justify-center gap-2 bg-[#FF7F00] hover:bg-[#E67200] text-white px-6 py-3 rounded-xl font-semibold text-base transition-all shadow-sm hover:shadow-md active:scale-[0.98]" data-testid="calc-consultation-btn">
                    {t('implementation.calculator.consultationBtn')} <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========== ENTERPRISE SCALE ========== */}
      <section className="section-padding bg-slate-50" data-testid="enterprise-scale-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-caption text-[#0077FF] mb-4 block">{t('implementation.enterpriseScale.label')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('implementation.enterpriseScale.title')}</h2>
            <p className="text-lg text-slate-600">{t('implementation.enterpriseScale.description')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {scaleMetrics.map((metric, index) => {
              const Ic = scaleIcons[index] || Globe;
              const colors = [
                { bg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', border: 'border-[#0077FF]/20' },
                { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
                { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' }
              ];
              const c = colors[index];
              return (
                <div key={index} className={`bg-white rounded-2xl p-8 border-2 ${c.border} text-center hover:shadow-lg transition-all`}>
                  <div className={`w-16 h-16 ${c.bg} rounded-2xl flex items-center justify-center mx-auto mb-5`}>
                    <Ic className={`w-8 h-8 ${c.text}`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{metric.title}</h3>
                  <p className="text-slate-600">{metric.text}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center">
            <p className="text-lg md:text-xl font-medium text-slate-700 max-w-3xl mx-auto">{t('implementation.enterpriseScale.closing')}</p>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-20 bg-slate-900" data-testid="implementation-closing-section">
        <div className="container-max px-6 md:px-12 lg:px-24 text-center">
          <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight max-w-4xl mx-auto mb-6">
            {t('implementation.closing')}
          </p>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            {t('implementation.ctaSubtext')}
          </p>
          <Link to={getLocalizedPath('/contact')} className="btn-primary bg-white text-slate-900 hover:bg-slate-100 text-base" data-testid="implementation-cta">
            {t('implementation.ctaButton')} <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ImplementationPage;
